import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FiX, FiCheck, FiUpload } from 'react-icons/fi';
import { 
  AVATAR_CATEGORIES, 
  AVATAR_PRESETS, 
  COLOR_PALETTES, 
  IMAGE_FILTERS,
  getAvatarUrl,
  applyAvatarCustomization 
} from '../../utils/avatarConfig';
import './AvatarPickerModalStatic.css';

const AvatarPickerModal = ({ isOpen, onClose, value, onSelect, initialTab = 'pick' }) => {
  const [selectedCategory, setSelectedCategory] = useState('girls');
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    
    // Establecer pestaña inicial
    setTab(initialTab);
    
    // Si hay un valor previo, cargarlo
    if (value?.avatarId) {
      setSelectedAvatarId(value.avatarId);
      const preset = AVATAR_PRESETS.find(p => p.id === value.avatarId);
      if (preset) {
        setSelectedCategory(preset.category);
      }
      if (!value.isCustomPhoto) {
        setTab('pick');
      }
    } else if (value?.uploadedPhoto) {
      setUploadedPhoto(value.uploadedPhoto);
      setTab('upload');
    } else {
      // Avatar por defecto
      setSelectedAvatarId('girl-1');
      setSelectedCategory('girls');
    }
  }, [isOpen, value, initialTab]);

  const filteredAvatars = useMemo(() => {
    return AVATAR_PRESETS.filter(avatar => avatar.category === selectedCategory);
  }, [selectedCategory]);

  const currentAvatarUrl = useMemo(() => {
    if (tab === 'upload' && uploadedPhoto) {
      return uploadedPhoto;
    }
    return selectedAvatarId ? getAvatarUrl(selectedAvatarId) : getAvatarUrl('girl-1');
  }, [selectedAvatarId, uploadedPhoto, tab]);

  const handleSelectAvatar = (avatarId) => {
    setSelectedAvatarId(avatarId);
    setUploadedPhoto(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen es muy grande. Por favor selecciona una imagen menor a 5MB.');
      return;
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen válida.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedPhoto(event.target.result);
      setSelectedAvatarId(null);
    };
    reader.readAsDataURL(file);
  };

  const handleApply = () => {
    if (tab === 'upload' && uploadedPhoto) {
      onSelect({
        uploadedPhoto: uploadedPhoto,
        url: uploadedPhoto,
        isCustomPhoto: true
      });
    } else {
      onSelect({
        avatarId: selectedAvatarId,
        url: currentAvatarUrl
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="avatar-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="avatar-modal-static" onClick={(e) => e.stopPropagation()}>
        <div className="avatar-modal-header">
          <h2 className="avatar-modal-title">Elige tu avatar</h2>
          <button type="button" className="avatar-modal-close" onClick={onClose} aria-label="Cerrar">
            <FiX />
          </button>
        </div>

        <div className="avatar-modal-body-static">
          {/* Vista previa */}
          <div className="avatar-modal-left">
            <div className="avatar-preview-large">
              <img 
                src={currentAvatarUrl} 
                alt="Vista previa del avatar"
              />
            </div>
            <button 
              type="button" 
              className="avatar-apply-btn"
              onClick={handleApply}
            >
              <FiCheck /> Aplicar avatar
            </button>
          </div>

          {/* Selector */}
          <div className="avatar-modal-right">
            <div className="avatar-tabs-static">
              <button
                type="button"
                className={`avatar-tab-static ${tab === 'pick' ? 'active' : ''}`}
                onClick={() => setTab('pick')}
              >
                Elegir Avatar
              </button>
              <button
                type="button"
                className={`avatar-tab-static ${tab === 'upload' ? 'active' : ''}`}
                onClick={() => setTab('upload')}
              >
                <FiUpload /> Subir Foto
              </button>
            </div>

            {tab === 'pick' && (
              <div className="avatar-pick-content">
                {/* Categorías */}
                <div className="avatar-categories">
                  {Object.entries(AVATAR_CATEGORIES).map(([key, cat]) => (
                    <button
                      key={key}
                      type="button"
                      className={`category-btn ${selectedCategory === key ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(key)}
                      title={cat.description}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Grid de avatares */}
                <div className="avatar-grid-static">
                  {filteredAvatars.map((avatar) => (
                    <button
                      key={avatar.id}
                      type="button"
                      className={`avatar-grid-item-static ${selectedAvatarId === avatar.id ? 'selected' : ''}`}
                      onClick={() => handleSelectAvatar(avatar.id)}
                      title={avatar.label}
                    >
                      <img src={getAvatarUrl(avatar.id)} alt={avatar.label} />
                      {selectedAvatarId === avatar.id && (
                        <div className="avatar-selected-badge">
                          <FiCheck />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {tab === 'upload' && (
              <div className="avatar-upload-content">
                <div className="upload-instructions">
                  <FiUpload size={48} />
                  <h3>Sube tu propia foto</h3>
                  <p>Selecciona una imagen desde tu computadora</p>
                  <ul>
                    <li>Formato: JPG, PNG o GIF</li>
                    <li>Tamaño máximo: 5 MB</li>
                    <li>Recomendado: Foto cuadrada</li>
                  </ul>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                <button
                  type="button"
                  className="upload-photo-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiUpload /> Seleccionar Imagen
                </button>

                {uploadedPhoto && (
                  <div className="uploaded-preview">
                    <p>✓ Imagen cargada correctamente</p>
                    <button
                      type="button"
                      className="change-photo-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Cambiar imagen
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarPickerModal;
